import React, { useMemo, useState } from 'react';
import { MapPin, ChevronDown, Home, FileText, AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useImoveisArea } from '../../hooks/useImoveisArea';
import { usePropostasOferta } from '../../hooks/usePropostasOferta';
import ImovelDetailModal from './ImovelDetailModal';

const SITUACAO_LABEL = {
  in_negotiation: 'negociando',
  sold: 'vendido',
  expropriated: 'desapropriado',
  judicial: 'judicial',
  archived: 'arquivado',
};
const SITUACAO_COLOR = {
  in_negotiation: 'bg-amber-100 text-amber-700',
  sold: 'bg-emerald-100 text-emerald-700',
  expropriated: 'bg-red-100 text-red-700',
  judicial: 'bg-red-100 text-red-700',
  archived: 'bg-slate-200 text-slate-600',
};

/**
 * Bloco de 1 imovel — Intake-1: propostas saíram daqui (Zona 3 do modal).
 * Mostra apenas asset (imóvel + proprietários) + contagem de propostas como link sutil.
 */
const ImovelBlock = ({ imovel, proprietarios, propostasCount, onOpenFicha }) => {
  const sitLabel = SITUACAO_LABEL[imovel.situacao];
  const sitColor = SITUACAO_COLOR[imovel.situacao] || 'bg-slate-100 text-slate-600';

  return (
    <div className="px-4 py-3 border-b border-slate-100 last:border-b-0">
      {/* Header do imovel */}
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <Home size={13} className="text-slate-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-slate-800 truncate">{imovel.nome}</span>
          {imovel.area_terreno_m2 && (
            <span className="text-xs text-slate-500">
              {Number(imovel.area_terreno_m2).toLocaleString('pt-BR')}m²
            </span>
          )}
          {sitLabel && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${sitColor}`}>{sitLabel}</span>
          )}
          {imovel.matricula && (
            <span className="text-[10px] text-slate-400" title="Matrícula">Mat. {imovel.matricula}</span>
          )}
          {propostasCount > 0 && (
            <span
              className="text-[10px] text-indigo-600 flex items-center gap-0.5"
              title="Veja as propostas na Zona Transação acima"
            >
              <FileText size={10} /> {propostasCount} proposta{propostasCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={onOpenFicha}
          className="text-[10px] text-indigo-600 hover:text-indigo-800 px-2 py-0.5 hover:bg-indigo-50 rounded flex items-center gap-0.5 flex-shrink-0"
        >
          ver ficha <ArrowRight size={10} />
        </button>
      </div>

      {/* Proprietarios */}
      {proprietarios.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {proprietarios.map(p => {
            const nome = p.empresa?.empresa
              || p.empresa?.company_name
              || [p.contato?.first_name, p.contato?.last_name].filter(Boolean).join(' ')
              || '—';
            const pct = Number(p.percentual_participacao);
            return (
              <span
                key={p.id}
                className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded flex items-center gap-1"
                title={p.tipo_propriedade ? `Tipo: ${p.tipo_propriedade}` : undefined}
              >
                👤 {nome}
                {isFinite(pct) && pct < 100 && (
                  <span className="text-slate-400">({pct}%)</span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * AreaImoveisPropostasSection (Fase C — cascata hierarquica).
 *
 * Substitui PropostasSection no OfertaDetailModal quando a oferta tem area_id.
 * Hierarquia: ÁREA → IMÓVEIS da área → PROPOSTAS desta oferta agrupadas por
 * imovel_id (via proposta_imoveis). Propostas órfãs (sem imóvel vinculado)
 * ficam em bloco no rodapé como fallback.
 *
 * Props:
 *   ofertaId, areaId (obrigatórios para cascata)
 *   areaNome, areaM2 (opcional — passados pelo OfertaDetailModal a partir
 *     de oferta.areas.{nome, area_total_m2})
 *   empresaId (default proponente em novas propostas)
 *   userEmail (audit)
 */
const AreaImoveisPropostasSection = ({
  ofertaId, areaId, areaNome, areaM2,
}) => {
  const { imoveis: areaImoveis, loading: loadingArea, error: errorArea } = useImoveisArea(areaId);
  const { propostas, loading: loadingProp, error: errorProp } = usePropostasOferta(ofertaId);
  const [expanded, setExpanded] = useState(true);
  const [imovelModalId, setImovelModalId] = useState(null);

  // Apenas conta quantas propostas referenciam cada imovel (Intake-1: o card
  // de proposta vive na Zona 3, aqui só sinalizamos quantas tocam o imovel).
  const propostasCountByImovel = useMemo(() => {
    const map = new Map();
    for (const p of propostas) {
      for (const pi of (p.proposta_imoveis || [])) {
        const iid = pi.imovel?.id;
        if (!iid) continue;
        map.set(iid, (map.get(iid) || 0) + 1);
      }
    }
    return map;
  }, [propostas]);

  const loading = loadingArea || loadingProp;
  const error = errorArea || errorProp;
  const headerCount = areaImoveis?.length || 0;

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-t-lg text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
      >
        <span className="flex items-center gap-1.5 flex-wrap">
          <MapPin size={14} />
          Área: {areaNome || areaId}
          {areaM2 && (
            <span className="text-emerald-600 font-normal">
              ({Number(areaM2).toLocaleString('pt-BR')}m²)
            </span>
          )}
          <span className="text-emerald-600 font-normal">
            · {headerCount} imóvel{headerCount !== 1 ? 'eis' : ''}
          </span>
        </span>
        <ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border border-t-0 border-emerald-200 rounded-b-lg bg-white">
          {error && (
            <div className="m-3 flex items-start gap-1.5 p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
              <AlertCircle size={11} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {loading && (
            <div className="p-3 flex items-center gap-2 text-xs text-slate-500">
              <Loader2 size={12} className="animate-spin" /> Carregando imóveis da área...
            </div>
          )}

          {!loading && areaImoveis.length === 0 && (
            <div className="p-3 text-xs text-slate-400 italic">
              Nenhum imóvel cadastrado nesta área ainda.
            </div>
          )}

          {!loading && areaImoveis.map(ai => {
            const imovel = ai.imovel;
            if (!imovel) return null;
            const proprietarios = imovel.proprietarios || [];
            return (
              <ImovelBlock
                key={imovel.id}
                imovel={imovel}
                proprietarios={proprietarios}
                propostasCount={propostasCountByImovel.get(imovel.id) || 0}
                onOpenFicha={() => setImovelModalId(imovel.id)}
              />
            );
          })}
        </div>
      )}

      {imovelModalId && (
        <ImovelDetailModal
          imovelId={imovelModalId}
          onClose={() => setImovelModalId(null)}
        />
      )}
    </div>
  );
};

export default AreaImoveisPropostasSection;
